<template>
  <TransitionRoot appear :show="modelValue" as="template">
    <Dialog as="div" @close="handleClose" :class="['relative', zIndexClass]">
      <TransitionChild
        as="template"
        enter="duration-300 ease-out"
        enter-from="opacity-0"
        enter-to="opacity-100"
        leave="duration-200 ease-in"
        leave-from="opacity-100"
        leave-to="opacity-0"
      >
        <div class="fixed inset-0 bg-black/25 backdrop-blur-sm" />
      </TransitionChild>

      <div class="fixed inset-0 overflow-y-auto">
        <div class="flex min-h-full items-center justify-center p-4">
          <TransitionChild
            as="template"
            enter="duration-300 ease-out"
            enter-from="opacity-0 scale-95"
            enter-to="opacity-100 scale-100"
            leave="duration-200 ease-in"
            leave-from="opacity-100 scale-100"
            leave-to="opacity-0 scale-95"
          >
            <DialogPanel
              :class="[
                'w-full transform overflow-hidden rounded-2xl bg-white px-4 py-6 shadow-2xl transition-all',
                maxWidthClass,
              ]"
            >
              <DialogTitle
                v-if="title"
                class="text-lg font-semibold leading-normal text-text-primary mb-4"
              >
                {{ title }}
              </DialogTitle>

              <div class="min-w-0 text-sm text-text-secondary">
                <slot />
              </div>

              <div v-if="showActions" class="mt-6 flex gap-4 justify-end">
                <Button variant="outline" @click="handleClose">
                  {{ cancelText }}
                </Button>
                <Button variant="primary" @click="handleConfirm">
                  {{ confirmText }}
                </Button>
              </div>
            </DialogPanel>
          </TransitionChild>
        </div>
      </div>
    </Dialog>
  </TransitionRoot>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { Dialog, DialogPanel, DialogTitle, TransitionRoot, TransitionChild } from '@headlessui/vue'
import Button from './Button.vue'

interface Props {
  modelValue: boolean
  title?: string
  showActions?: boolean
  confirmText?: string
  cancelText?: string
  maxWidth?: 'sm' | 'md' | 'lg' | 'xl' | '2xl' | 'full'
  zIndexClass?: string
}

const props = withDefaults(defineProps<Props>(), {
  showActions: true,
  confirmText: 'Confirm',
  cancelText: 'Cancel',
  maxWidth: 'md',
  zIndexClass: 'z-[10050]',
})

const maxWidthClass = computed((): string => {
  const classes: Record<'sm' | 'md' | 'lg' | 'xl' | '2xl' | 'full', string> = {
    sm: 'max-w-sm',
    md: 'max-w-md',
    lg: 'max-w-lg',
    xl: 'max-w-xl',
    '2xl': 'max-w-2xl',
    full: 'max-w-full',
  }
  return classes[props.maxWidth]
})

const emit = defineEmits<{
  'update:modelValue': [value: boolean]
  close: []
  confirm: []
}>()

const handleClose = (): void => {
  emit('update:modelValue', false)
  emit('close')
}

const handleConfirm = (): void => {
  emit('confirm')
}
</script>
